package com.yogaaicoach.pages.trainingprogram

import com.yogaaicoach.common.ui.components.*
import com.yogaaicoach.pages.dashboard.mainGoalDisplayName
import com.yogaaicoach.scope
import kotlinx.coroutines.launch
import react.ChildrenBuilder
import react.FC
import react.Props
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.h1
import react.dom.html.ReactHTML.p
import react.dom.html.ReactHTML.span
import react.useEffectOnce
import react.useState
import web.cssom.ClassName

internal external interface AddNewTrainingScreen1PageProps : Props {
    var viewModel: AddNewTrainingViewModel
    var onNextButtonClicked: () -> Unit
}

private data class GoalContent(val goal: MainGoal, val image: String, val description: String)

private val goalContents = listOf(
    GoalContent(
        MainGoal.PHYSICAL_STRENGTH,
        "physical_strength.jpg",
        "Focuses on building and enhancing muscle strength, endurance, and overall physical power. \nThis includes upper body, core, and lower body strength, as well as muscle tone. \n\nIdeal for those looking to improve their physical fitness and challenge their body through dynamic movements and poses."
    ),
    GoalContent(
        MainGoal.BALANCE_AND_COORDINATION,
        "balance_and_coordination.jpg",
        "Emphasizes improving balance, stability, and coordination through controlled movements and poses. \nThis group helps enhance body awareness and control. \n\nSuitable for those wanting to improve their physical equilibrium and precision in movements."
    ),
    GoalContent(
        MainGoal.POSTURE_AND_ALIGNMENT,
        "posture_and_alignment.jpg",
        "Concentrates on improving body posture, alignment, and structural integrity. \nThis group focuses on proper alignment in poses to prevent injuries and correct postural imbalances. \n\nSuitable for those looking to enhance their posture, reduce pain, and improve overall body mechanics."
    ),
    GoalContent(
        MainGoal.MOBILITY_AND_FLEXIBILITY,
        "mobility_and_flexibility.jpg",
        "Focuses on increasing joint mobility, flexibility, and range of motion. \nThis group includes deep stretching, gentle twists, and flexibility exercises to improve overall mobility and prevent stiffness. \n\nIdeal for those aiming to enhance their flexibility and maintain joint health."
    ),
)

// Pixel-matched against AddNewTrainingScreen1.kt + AddTrainingRow.kt + StepProgressIndicator.kt.
// LazyPageLayout's bottomContent is sticky-at-viewport-bottom on small screens and a normal
// inline list item on large screens — handled with a CSS position swap (.wizard-bottom-bar),
// same approach as other small/large layout differences in this port.
internal val AddNewTrainingScreen1Page = FC<AddNewTrainingScreen1PageProps> { props ->
    var selectedGoals by useState(props.viewModel.selectedGoals.value)

    useEffectOnce {
        scope.launch { props.viewModel.selectedGoals.collect { selectedGoals = it } }
    }

    div {
        className = ClassName("wizard-page wizard-page-has-bottom-bar")

        StepProgressIndicator(1, listOf(CenterFocusStrongIcon, TrendingUpIcon, PersonIcon, CalendarMonthIcon))

        PageTitleSection(
            "Choose your main focus areas",
            "Select the key areas you want to concentrate on in your yoga practice."
        )

        div {
            className = ClassName("wizard-list")
            goalContents.forEach { content ->
                val displayName = mainGoalDisplayName(content.goal.name)
                AddTrainingRow(content.image, displayName, content.description, selectedGoals.contains(content.goal)) {
                    props.viewModel.toggleSelectedGoal(content.goal)
                }
            }
        }

        div {
            className = ClassName("wizard-bottom-bar")
            button {
                className = ClassName("primary-button")
                disabled = selectedGoals.isEmpty()
                onClick = { props.onNextButtonClicked() }
                span { +"Next" }
                ArrowForwardIconWhite {}
            }
        }
    }
}

internal fun ChildrenBuilder.PageTitleSection(title: String, description: String, hint: String? = null) {
    div {
        className = ClassName(if (hint != null) "wizard-page-title wizard-page-title-with-hint" else "wizard-page-title")
        h1 { +title }
        p { className = ClassName("wizard-page-title-description"); +description }
        if (hint != null) {
            p { className = ClassName("wizard-page-title-hint"); +hint }
        }
    }
}
